import { useState, useCallback, useMemo } from "react";

const STORAGE_KEY_PREFIX = "wondermaps:bookmarks:";

/**
 * Hook to manage visitor bookmarks per organization.
 * Stores bookmarked POI IDs in localStorage.
 */
export function useBookmarks(orgSlug: string) {
  const storageKey = `${STORAGE_KEY_PREFIX}${orgSlug}`;

  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return new Set();
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) {
        // Corrupted data - clear it
        localStorage.removeItem(storageKey);
        return new Set();
      }
      // Validate all items are strings
      const validIds = parsed.filter((id): id is string => typeof id === "string");
      return new Set(validIds);
    } catch {
      // Parse error - clear corrupted data
      localStorage.removeItem(storageKey);
      return new Set();
    }
  });

  const persist = useCallback(
    (ids: Set<string>) => {
      if (typeof window === "undefined") return;
      localStorage.setItem(storageKey, JSON.stringify([...ids]));
    },
    [storageKey]
  );

  const toggleBookmark = useCallback(
    (poiId: string) => {
      setBookmarkedIds((prev) => {
        const next = new Set(prev);
        if (next.has(poiId)) {
          next.delete(poiId);
        } else {
          next.add(poiId);
        }
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const isBookmarked = useCallback(
    (poiId: string) => bookmarkedIds.has(poiId),
    [bookmarkedIds]
  );

  const loadCuratedList = useCallback(
    (poiIds: string[]) => {
      setBookmarkedIds((prev) => {
        const next = new Set(prev);
        poiIds.forEach((id) => next.add(id));
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const bookmarkCount = useMemo(() => bookmarkedIds.size, [bookmarkedIds]);

  return {
    bookmarkedIds,
    toggleBookmark,
    isBookmarked,
    bookmarkCount,
    loadCuratedList,
  };
}
